import { create } from 'zustand';
import { authService } from '@/services/auth';
import { syncService } from '@/services/sync';
import { gpsService } from '@/services/gps';
import { logQueueService } from '@/services/logQueue';
import { LogTypes, type DriverProfile } from '@/types/domain';
import { StorageKeys } from '@/lib/config';

interface AuthState {
  isLoggedIn: boolean;
  driver: DriverProfile | null;
  loading: boolean;
  error: string | null;
  login: (driverNumber: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hydrate: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  isLoggedIn: false,
  driver: null,
  loading: false,
  error: null,

  hydrate: () => {
    const driverNumber = localStorage.getItem(StorageKeys.driverNumber);
    if (driverNumber && authService.isLoggedIn()) {
      set({ isLoggedIn: true, driver: { driverNumber } });
      // Resume route-level GPS tracking on reload/resume. The tracker is
      // self-selecting (only logs `gps break` for active switch 2/5 bills), so
      // it safely idles if the driver has nothing in progress.
      gpsService.startRouteTracking();
    }
  },

  login: async (driverNumber, password) => {
    set({ loading: true, error: null });
    try {
      const driver = await authService.login(driverNumber, password);
      set({ loading: false, isLoggedIn: true, driver });
      // Session audit log: LOG IN with user + device date/time (Martin).
      void logQueueService.enqueue({ logType: LogTypes.login, display: 'Display' });
      void syncService.runOnce('login');
    } catch (err) {
      const message = (err as Error)?.message ?? 'Login failed';
      set({ loading: false, error: message, isLoggedIn: false, driver: null });
      throw err;
    }
  },

  logout: async () => {
    // Session audit log: LOG OUT. Enqueue and flush while we still hold a valid
    // token; if offline it stays queued and pushes on the next authenticated sync.
    try {
      await logQueueService.enqueue({ logType: LogTypes.logout, display: 'Display' });
      await logQueueService.flush();
    } catch {
      // Never block sign-out on the audit log.
    }
    // Stop push nudges for this device so a signed-out phone is not notified
    // about the next driver's work. Best-effort; must run while authenticated.
    try {
      const { pushService } = await import('@/services/push');
      await pushService.disable();
    } catch {
      // Never block sign-out on push cleanup.
    }
    authService.logout();
    syncService.stop();
    void gpsService.stopRouteTracking();
    set({ isLoggedIn: false, driver: null, error: null });
  },
}));
